import React, { useEffect, useState } from "react"
import { useIntl, FormattedMessage } from 'react-intl'
import "./Register.sass"
import { useLocation } from "react-router-dom"
import { authenticatedRegistration, validateRegistrationToken } from "../../hooks/api2"
import imageLogo from "../../static/logo-adigital-white.png"

const Register = () => {
  const useQuery = () => {
    const { search } = useLocation()
    return React.useMemo(() => new URLSearchParams(search), [search])
  }

  const intl = useIntl()
  let query = useQuery()
  const domain = query.get('domain')

  const customizations = {
    'www.iamericas.ai': 'iamericas',
    'default': 'default'
  }
  const style = customizations[domain] || customizations.default

  let [parsedToken, setParsedToken] = useState(null)
  let [registerDone, setRegisterDone] = useState(false)
  let [sendingRequest, setSendingRequest] = useState(false)
  let [registerFail, setRegisterFail] = useState(false)
  let [registrationLinkError, setRegistrationLinkError] = useState(false)

  let [formName, setFormName] = useState("")
  let [formSurname, setFormSurname] = useState("")
  let [formPosition, setFormPosition] = useState("")
  let [formManualCompany, setFormManualCompany] = useState("")
  let [formAccountIndex, setFormAccountIndex] = useState("-1")

  // SSO Token validation
  useEffect(async () => {
    if (!parsedToken) {
      const registrationToken = query.get("registrationToken")

      if (registrationToken) {
        const req = await validateRegistrationToken({ registrationToken })

        if (req.ok) {
          const token = JSON.parse(Buffer.from(registrationToken.split(".")[1], "base64").toString("utf8"))
          if (token && token.sub && token.accountsData) setParsedToken(token)
          else setRegistrationLinkError(true)
        } else {
          if (req.status === 403) setRegistrationLinkError(true)
          else setRegisterFail(true)
        }
      } else setRegistrationLinkError(true)
    }
  })

  const registerFormSubmit = async (e) => {
    setSendingRequest(true)
    e.preventDefault()

    const res = await authenticatedRegistration({
      registrationToken: query.get("registrationToken"),
      contactName: formName,
      contactSurname: formSurname,
      contactPosition: formPosition,
      contactManualCompany: formManualCompany,
      contactAccountIndex: formAccountIndex
    })

    if (res.status === 200) {
      const resBody = await res.json()

      const redirect = query.get('redirect')

      const redirectLink = new URL(`${window.location.protocol}//${window.location.host}/login2`)
      redirectLink.searchParams.append('ssoToken', resBody.token)
      if (redirect) redirectLink.searchParams.append('redirect', redirect)
      if (domain) redirectLink.searchParams.append('domain', domain)

      window.location.replace(redirectLink)
    } else {
      setRegisterFail(true)
    }
  }

  const registerFailPage = () => {
    return (
      <div className="form-content">
        <h2><FormattedMessage id={`register.${style}.error.title`} /></h2>
        <p>
          <FormattedMessage id={`register.${style}.error.message`} values={
            {
              reload: <a href="" onClick={() => window.location.reload()}>
                <FormattedMessage id={`register.${style}.error.message.reload`} />
              </a>
            }
          } />
        </p>
        <p>
          <FormattedMessage id={`register.${style}.error.help`} values={
            {
              mailto: <FormattedMessage id={`register.${style}.error.help.mailto`}>
                {
                  (mailtoaddress) => <a href={`mailto:${mailtoaddress}`}>
                    {mailtoaddress}
                  </a>
                }
              </FormattedMessage>
            }
          } />
        </p>
      </div>
    )
  }

  const registerFormPage = () => {
    const email = parsedToken ? parsedToken.sub : intl.formatMessage({ id: 'register.default.form.placeholder.requesting' })
    const accountsData = parsedToken ? parsedToken.accountsData : null

    return (
      <form className="form" onSubmit={registerFormSubmit}>
        <div className="form-content">
          <p><FormattedMessage id={`register.${style}.form.message`} /></p>
          <FormattedMessage id={`register.${style}.form.placeholder.name`}>
            {
              (ph) => <input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={ph}
                disabled={sendingRequest}
                type="text"
              />
            }
          </FormattedMessage>
          <FormattedMessage id={`register.${style}.form.placeholder.surnames`}>
            {
              (ph) => <input
                value={formSurname}
                onChange={(e) => setFormSurname(e.target.value)}
                placeholder={ph}
                disabled={sendingRequest}
                type="text"
              />
            }
          </FormattedMessage>
          <FormattedMessage id={`register.${style}.form.placeholder.position`}>
            {
              (ph) => <input
                value={formPosition}
                onChange={(e) => setFormPosition(e.target.value)}
                placeholder={ph}
                disabled={sendingRequest}
                type="text"
              />
            }
          </FormattedMessage>

          <input value={email} readOnly={true} disabled={true} type="text" />


          {accountsData && accountsData.length === 0 ?
            <FormattedMessage id={`register.${style}.form.placeholder.manualCompany`}>
              {
                (ph) => <input
                  value={formManualCompany}
                  onChange={(e) => setFormManualCompany(e.target.value)}
                  placeholder={ph}
                  disabled={sendingRequest}
                  type="text"
                />
              }
            </FormattedMessage>
            :
            <select
              value={formAccountIndex}
              disabled={!accountsData || sendingRequest}
              onChange={(e) => setFormAccountIndex(e.target.value)}
            >
              <option value={-1}>
                {accountsData ?
                  intl.formatMessage({ id: 'register.default.form.placeholder.company.pick' }) :
                  intl.formatMessage({ id: 'register.default.form.placeholder.requesting' })
                }
              </option>
              {accountsData &&
                accountsData.map((account, i) => (
                  <option key={i} value={i}>
                    {`${account.name} (${account.corporate_name})`}
                  </option>
                ))
              }
            </select>
          }
          <button
            disabled={
              (formAccountIndex === "-1" && !formManualCompany) ||
              formPosition === "" ||
              formName === "" ||
              formSurname === "" ||
              sendingRequest === true
            }
            className={
              `${(formAccountIndex === "-1" && !formManualCompany) ||
                formPosition === "" ||
                formName === "" ||
                formSurname === "" ||
                sendingRequest === true
                ? "empty-button"
                : "ready-button"
              } ${style}`
            }
            type="submit"
          >
            {sendingRequest ?
              <FormattedMessage id={`register.${style}.form.submitting`} /> :
              <FormattedMessage id={`register.${style}.form.submit`} />
            }
          </button>
        </div>
      </form>
    )
  }

  const registerDonePage = () => {
    return (
      <div className="form-content">
        <h2><FormattedMessage id={`register.${style}.done.title`} /></h2>
        <p><FormattedMessage id={`register.${style}.done.message`} /></p>

        <p className="smaller-paragraph">
          <FormattedMessage id={`register.${style}.done.info`} values={
            {
              reload: <a href="" onClick={() => window.location.reload()}>
                <FormattedMessage id={`register.${style}.done.info.reload`} />
              </a>,
              mailto: <FormattedMessage id={`register.${style}.done.info.mailto`}>
                {
                  (mailtoaddress) => <a href={`mailto:${mailtoaddress}`}>
                    {mailtoaddress}
                  </a>
                }
              </FormattedMessage>
            }
          } />
        </p>
      </div>
    )
  }

  const registrationLinkErrorPage = () => {
    return (
      <div className="form-content">
        <h2><FormattedMessage id={`register.${style}.autherror.title`} /></h2>
        <p><FormattedMessage id={`register.${style}.autherror.message`} /></p>
        <p>
          <FormattedMessage id={`register.${style}.autherror.help`} values={
            {
              mailto: <FormattedMessage id={`register.${style}.autherror.help.mailto`}>
                {
                  (mailtoaddress) => <a href={`mailto:${mailtoaddress}`}>
                    {mailtoaddress}
                  </a>
                }
              </FormattedMessage>
            }
          } />
        </p>
      </div>
    )
  }

  const selectContent = () => {
    if (registerFail) return registerFailPage()
    else if (registerDone) return registerDonePage()
    else if (registrationLinkError) return registrationLinkErrorPage()
    else return registerFormPage()
  }

  return (
    <div id="register" className={style}>
      {style === 'default' && //TODO move to CSS
        <div className="logo">
          <img src={imageLogo} />
        </div>
      }
      <div className="register-content">
        <div className="form-register">

          {selectContent()}

        </div>
        <div className={`login-image ${style}`} />
      </div>
    </div>
  )

}

export default Register